import React, { useState, useEffect } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import StyledDialog from "../StyledDialog";
import TeacherSelect from "../TeacherSelect";
import ThumbnailField from "../ThumbnailField";
import PdfUploadField from "../PdfUploadField";
import { usePdfUpload } from "../../hooks/usePdfUpload";
import { buildMutualLectureFileName } from "../../lib/pdfFileName";
import { dateChange } from "../../lib/dates";
import { URL_TYPE_OPTIONS, MUTUAL_EXAM_TYPE_OPTIONS, PAKODEMY_TEACHER_OPTION } from "../../constants";
import { ApiUrlAdmin } from "../../../../api/common/Config";
import ResponseStatus from "../../../../util/enums";

// "Rehberlik ve ortak dersler ekle" — general lectures, not curriculum-tied.
// Logic is unchanged from legacy: hardcoded ExamType options (99 TYT-AYT / 3
// LGS), free-text titles, addGeneralLecture endpoint, and the synthetic
// "Pakodemy Hoca" (Id 0) teacher option for multi-teacher lectures.
//
// duplicateSource (optional): an existing general lecture to copy — all fields
// prefilled except the date (the field a duplicate exists to change) and the
// attestation checkbox. The source PDF is adopted as already-uploaded; the
// thumbnail is fetched back into a File.
const AddMutualLectureModal = ({
    open,
    onClose,
    teachers,
    adminToken,
    urlType,
    onUrlTypeChange,
    onAdded,
    duplicateSource,
}) => {
    const pdf = usePdfUpload(adminToken);
    const source = duplicateSource || null;
    const sourceSubTopicName = source
        ? source.SubTopicName && source.SubTopicName.includes("/")
            ? source.SubTopicName.split("/")[1].trim()
            : source.SubTopicName || ""
        : "";
    const [examTypeId, setExamTypeId] = useState(source ? String(source.ExamCategoryId) : ""); // radio: 1 YKS / 2 LGS
    const [examCategory, setExamCategory] = useState(source ? String(source.ExamType) : ""); // 99 or 3
    const [selectedTeacher, setSelectedTeacher] = useState(
        source && source.TeacherInfos ? String(source.TeacherInfos.TeacherId) : ""
    );
    const [startDate, setStartDate] = useState(source && source.Date ? new Date(source.Date) : new Date());
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [isPublic, setIsPublic] = useState(source ? Boolean(source.IsPublic) : false);
    const [title, setTitle] = useState(source ? source.Title || "" : "");
    const [topicName, setTopicName] = useState(source ? source.TopicName || "" : "");
    const [subTopicName, setSubTopicName] = useState(sourceSubTopicName);
    const [streamUrl, setStreamUrl] = useState(source ? source.StreamUrl || "" : "");
    const [offlineUrl, setOfflineUrl] = useState(source ? source.OfflineUrl || "" : "");
    // Local so a duplicate can start from the source's url type; user changes
    // still persist to the sticky page default via onUrlTypeChange
    const [urlTypeValue, setUrlTypeValue] = useState(
        source && source.OfflineUrlType != null ? source.OfflineUrlType : urlType
    );

    useEffect(() => {
        if (!source) return;
        if (source.LectureNoteUrl) {
            pdf.seedAsUploaded(source.LectureNoteUrl);
        }
        // if (source.ThumbnailUrl) {
        //     let cancelled = false;
        //     fetch(source.ThumbnailUrl)
        //         .then((res) => (res.ok ? res.blob() : Promise.reject(new Error("thumbnail fetch failed"))))
        //         .then((blob) => {
        //             if (cancelled) return;
        //             const file = new File([blob], "thumbnail.png", { type: blob.type || "image/png" });
        //             setThumbnailFile(file);
        //             setThumbnailPreview(URL.createObjectURL(file));
        //         })
        //         .catch(() => {
        //             // CORS or network failure: leave empty — the thumbnail gate
        //             // will require the operator to pick one manually
        //         });
        //     return () => {
        //         cancelled = true;
        //     };
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mutualOption = examTypeId ? MUTUAL_EXAM_TYPE_OPTIONS[examTypeId] : null;

    const pdfFileName = () => buildMutualLectureFileName({ title, topicName, subTopicName });

    const handleSubmit = async (event) => {
        event.preventDefault();

        // if (!pdf.pdfUrl || !pdf.isUploadedOk) {
        //     toast.error("pdf eklenmeden Ders ekleyemezsiniz");
        //     return;
        // }
        if (!thumbnailPreview) {
            toast.error("Thumbnail eklenmeden Ders ekleyemezsiniz");
            return;
        }

        const formData = new FormData();
        formData.append("Title", title);
        formData.append("TopicName", topicName);
        formData.append("SubTopicName", subTopicName);
        formData.append("StreamUrl", streamUrl);
        formData.append("OfflineUrl", offlineUrl);
        if (isPublic) formData.append("PublicStream", "on");
        // General lectures carry no curriculum ids — 0, as in legacy
        formData.append("SubCourseTopicUniqueId", 0);
        formData.append("ExamCategoryId", Number(examTypeId));
        formData.append("OnlineLectureTeacherId", Number(selectedTeacher));
        formData.append("CourseId", 0);
        formData.append("ExamType", Number(examCategory));
        formData.append("ChannelCode", "Web");
        formData.append("Date", dateChange(startDate));
        formData.append("ThumbnailUrl", thumbnailFile);
        formData.append("LectureNoteUrl", pdf.pdfUrl || "");
        formData.append("RowStatus", true);
        formData.append("IsPublic", isPublic);
        formData.append("OfflineUrlType", urlTypeValue);

        const headers = {
            "Access-Control-Allow-Origin": "*",
            Authorization: "Bearer " + adminToken,
            "Content-Type": "multipart/form-data",
        };
        try {
            const res = await Axios.post(ApiUrlAdmin + "OnlineLecture/addGeneralLecture", formData, { headers });
            if (res.status == 200) {
                if (res.data.ResponseStatus == ResponseStatus.Success) {
                    toast.success("Ders başarıyla eklenmiştir");
                    onAdded();
                } else if (res.data.ResponseStatus == ResponseStatus.Error) {
                    toast.error(res.data.ResponseMessage);
                }
            } else {
                toast.error("bir hata ile karşılaşıldı lütfen tekrar deneyiniz");
            }
        } catch (ex) {
            toast.error(ex.response && ex.response.data ? ex.response.data.Error : "Bir hata oluştu");
        }
        onClose();
    };

    // Free-text name fields feed the PDF filename — changing them after an
    // upload invalidates the PDF (legacy rule)
    const nameFieldChange = (setter) => (e) => {
        setter(e.target.value);
        pdf.invalidate(true);
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <div className="olp-theme olp-modal">
                <h2 className="olp-modal-title">{source ? "Dersi Çoğalt" : "Rehberlik ve Ortak Ders Ekle"}</h2>
                <p className="olp-modal-subtitle">
                    {source
                        ? "Bilgiler kaynak dersten kopyalandı — yeni tarihi seçmeniz yeterli"
                        : "Müfredattan bağımsız genel ders oluşturun"}
                </p>
                <form method="post" onSubmit={handleSubmit}>
                    <div className="olp-modal-section">
                        <p className="olp-modal-section-title">Sınıflandırma</p>
                        <div className="olp-field">
                            <div className="olp-radio-group">
                                <label>
                                    <input
                                        type="radio"
                                        name="ExamCategoryId"
                                        value="1"
                                        checked={examTypeId == "1"}
                                        onChange={(e) => {
                                            setExamTypeId(e.target.value);
                                            setExamCategory("");
                                        }}
                                    />
                                    YKS
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="ExamCategoryId"
                                        value="2"
                                        checked={examTypeId == "2"}
                                        onChange={(e) => {
                                            setExamTypeId(e.target.value);
                                            setExamCategory("");
                                        }}
                                    />
                                    LGS
                                </label>
                            </div>
                        </div>
                        {examTypeId && (
                            <div className="olp-field">
                                <label htmlFor="ExamCategoryoptions">Lütfen sınav türünü seçiniz: </label>
                                <select
                                    className="olp-select"
                                    id="ExamCategoryoptions"
                                    value={examCategory}
                                    onChange={(e) => {
                                        pdf.invalidate(true);
                                        setExamCategory(e.target.value);
                                    }}
                                >
                                    <option value="">Sınav türünü seçiniz</option>
                                    {mutualOption && <option value={mutualOption.value}>{mutualOption.label}</option>}
                                </select>
                            </div>
                        )}
                    </div>
                    {examCategory && (
                        <div className="olp-modal-section">
                            <p className="olp-modal-section-title">Öğretmen ve Tarih</p>
                            <div className="olp-field">
                                <label>Öğretmen Seçiniz: </label>
                                <TeacherSelect
                                    teachers={teachers}
                                    extraOptions={[PAKODEMY_TEACHER_OPTION]}
                                    value={selectedTeacher}
                                    onChange={(id) => setSelectedTeacher(String(id))}
                                />
                            </div>
                            {selectedTeacher && (
                                <div className="olp-datepicker">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        timeInputLabel="Time:"
                                        dateFormat="MM/dd/yyyy h:mm aa"
                                        showTimeInput
                                        inline
                                    />
                                </div>
                            )}
                            {startDate && selectedTeacher && (
                                <ThumbnailField
                                    previewUrl={thumbnailPreview}
                                    onSelect={(file, objectUrl) => {
                                        setThumbnailFile(file);
                                        setThumbnailPreview(objectUrl);
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {examCategory && thumbnailPreview && (
                        <div className="olp-modal-section">
                            <p className="olp-modal-section-title">Ders Detayları</p>
                            <div className="olp-field olp-field--check">
                                <input
                                    type="checkbox"
                                    checked={isPublic}
                                    name="PublicStream"
                                    id="PublicStream"
                                    onChange={() => setIsPublic(!isPublic)}
                                />
                                <label htmlFor="PublicStream">Herkese açık yayın</label>
                            </div>
                            <div className="olp-field">
                                <label>Url Tipi: </label>
                                <select
                                    className="olp-select olp-select--inline"
                                    value={urlTypeValue}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        setUrlTypeValue(value);
                                        onUrlTypeChange(value);
                                    }}
                                >
                                    {URL_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="olp-field">
                                <label htmlFor="Title">Ana Başlık Giriniz: </label>
                                <input
                                    className="olp-input"
                                    required
                                    id="Title"
                                    name="Title"
                                    type="text"
                                    value={title}
                                    onChange={nameFieldChange(setTitle)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="TopicName">Başlık Giriniz: </label>
                                <input
                                    className="olp-input"
                                    required
                                    id="TopicName"
                                    name="TopicName"
                                    type="text"
                                    value={topicName}
                                    onChange={nameFieldChange(setTopicName)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="SubTopicName">Alt Başlık Giriniz(part1,part2): </label>
                                <input
                                    className="olp-input"
                                    id="SubTopicName"
                                    name="SubTopicName"
                                    type="text"
                                    value={subTopicName}
                                    onChange={nameFieldChange(setSubTopicName)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="UrlInput">Castr Url Giriniz: </label>
                                <input
                                    className="olp-input"
                                    name="StreamUrl"
                                    type="text"
                                    id="UrlInput"
                                    value={streamUrl}
                                    onChange={(e) => setStreamUrl(e.target.value)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="OfflineUrlInput">YouTube Url Giriniz: </label>
                                <input
                                    className="olp-input"
                                    required
                                    name="OfflineUrl"
                                    type="text"
                                    id="OfflineUrlInput"
                                    value={offlineUrl}
                                    onChange={(e) => setOfflineUrl(e.target.value)}
                                />
                            </div>
                            <PdfUploadField pdf={pdf} onFileSelected={(file) => pdf.upload(file, pdfFileName())} />
                        </div>
                    )}
                    <div className="olp-modal-footer">
                        <button type="button" className="olp-btn" onClick={onClose}>
                            Vazgeç
                        </button>
                        <button type="submit" className="olp-btn olp-btn--primary" 
                        // disabled={pdf.submitLocked}
                        >
                            Ders Ekle
                        </button>
                    </div>
                </form>
            </div>
        </StyledDialog>
    );
};

export default AddMutualLectureModal;
